// ============================================================
//  SETTINGS PAGE — ADMIN DASHBOARD
//  Path: src/pages/SettingsPage.tsx
//  All admin roles can change their own password
//
//  FIX: Change Password re-authenticated with the current password
//  only. Once an account has an authenticator app enrolled, Firebase
//  answers that with auth/multi-factor-auth-required instead of
//  signing in, so the change always failed with the generic "Failed to
//  change password". The form now also asks for the 6-digit code when
//  the account has TOTP enrolled, and completes the second factor
//  before calling updatePassword.
// ============================================================

import React, { useState } from 'react'
import {
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  getMultiFactorResolver,
  TotpMultiFactorGenerator,
  MultiFactorError,
} from 'firebase/auth'
import QRCode from 'qrcode'
import { auth } from '../config/firebase'
import { useAuth } from '../context/AuthContext'

const SettingsPage: React.FC = () => {
  const { user, mfaEnrolled, beginTotpEnrollment, completeTotpEnrollment } = useAuth()
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword]         = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError]                     = useState('')
  const [successMsg, setSuccessMsg]           = useState('')
  const [isLoading, setIsLoading]             = useState(false)
  // Authenticator code for Change Password — see the header FIX note.
  // Shown whenever the account has TOTP enrolled; pwCodeRequested covers
  // the rare case where mfaEnrolled was stale and Firebase asked anyway.
  const [pwMfaCode, setPwMfaCode]             = useState('')
  const [pwCodeRequested, setPwCodeRequested] = useState(false)
  const showPwCode = mfaEnrolled || pwCodeRequested

  // ── Two-factor authentication (authenticator app / TOTP) ─────
  // Every admin role can set this up on their own account — unlike
  // password change above, this isn't blocked for super_admin, since
  // nothing here touches the Firebase-console-managed password.
  //
  // mfaStep tracks where the admin is in the flow:
  //   'idle'        — not set up, showing the "Set up" button
  //   'need-reauth' — beginTotpEnrollment hit auth/requires-recent-login;
  //                   asking for the current password to refresh the
  //                   session before Firebase will issue a new secret
  //   'scan'        — a fresh secret was issued; showing the QR code
  //                   and waiting for the 6-digit confirmation code
  //   'done'        — just enrolled, in this same page load
  const [mfaStep, setMfaStep]                 = useState<'idle' | 'need-reauth' | 'scan' | 'done'>('idle')
  const [mfaReauthPassword, setMfaReauthPassword] = useState('')
  const [mfaQrDataUrl, setMfaQrDataUrl]        = useState('')
  const [mfaSecretKey, setMfaSecretKey]        = useState('')
  const [mfaCode, setMfaCode]                  = useState('')
  const [mfaError, setMfaError]                = useState('')
  const [mfaBusy, setMfaBusy]                  = useState(false)

  // The QR image itself is generated locally from the otpauth:// URL
  // Firebase returns — this never leaves the browser and no external
  // QR-code service ever sees the secret. See AuthContext.tsx's
  // beginTotpEnrollment for why the raw secret is otherwise only ever
  // shown as the manual-entry fallback text below the QR code.
  const startMfaSetup = async () => {
    setMfaError('')
    setMfaBusy(true)
    try {
      const { qrCodeUrl, secretKey } = await beginTotpEnrollment()
      const dataUrl = await QRCode.toDataURL(qrCodeUrl)
      setMfaQrDataUrl(dataUrl)
      setMfaSecretKey(secretKey)
      setMfaStep('scan')
    } catch (err: any) {
      if (err?.code === 'auth/requires-recent-login') {
        setMfaStep('need-reauth')
      } else {
        setMfaError(err?.message || 'Could not start setup — try again.')
      }
    } finally {
      setMfaBusy(false)
    }
  }

  const submitMfaReauth = async (e: React.FormEvent) => {
    e.preventDefault()
    setMfaError('')
    if (!auth.currentUser?.email) { setMfaError('Not signed in.'); return }
    setMfaBusy(true)
    try {
      const credential = EmailAuthProvider.credential(auth.currentUser.email, mfaReauthPassword)
      await reauthenticateWithCredential(auth.currentUser, credential)
      setMfaReauthPassword('')
      // Session is fresh now — retry the same call that asked for it.
      await startMfaSetup()
    } catch (err: any) {
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setMfaError('Incorrect password')
      } else {
        setMfaError('Could not verify your password — try again')
      }
      setMfaBusy(false)
    }
  }

  const submitMfaCode = async (e: React.FormEvent) => {
    e.preventDefault()
    setMfaError('')
    if (mfaCode.length !== 6) return
    setMfaBusy(true)
    try {
      await completeTotpEnrollment(mfaCode)
      setMfaStep('done')
      setMfaCode('')
      setMfaQrDataUrl('')
      setMfaSecretKey('')
    } catch (err: any) {
      if (err.code === 'auth/invalid-verification-code') {
        setMfaError('That code is incorrect or has expired — check the time on your device and try the next code.')
      } else {
        setMfaError('Could not confirm that code — try again.')
      }
      setMfaCode('')
    } finally {
      setMfaBusy(false)
    }
  }

  const cancelMfaSetup = () => {
    setMfaStep('idle')
    setMfaReauthPassword('')
    setMfaQrDataUrl('')
    setMfaSecretKey('')
    setMfaCode('')
    setMfaError('')
  }

  const getAdminRole = () => {
    if (user?.roles.includes('super_admin')) return 'Super Admin'
    if (user?.roles.includes('admin')) return 'Admin'
    if (user?.roles.includes('customer_service')) return 'Customer Service'
    return ''
  }

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccessMsg('')

    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters')
      return
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match')
      return
    }
    if (currentPassword === newPassword) {
      setError('New password must be different from current password')
      return
    }
    if (showPwCode && pwMfaCode.length !== 6) {
      setError('Enter the 6-digit code from your authenticator app')
      return
    }

    setIsLoading(true)
    try {
      const firebaseUser = auth.currentUser
      if (!firebaseUser || !firebaseUser.email) throw new Error('Not authenticated')

      // Re-authenticate first (required by Firebase for password change)
      const credential = EmailAuthProvider.credential(firebaseUser.email, currentPassword)
      try {
        await reauthenticateWithCredential(firebaseUser, credential)
      } catch (reauthErr: any) {
        if (reauthErr?.code !== 'auth/multi-factor-auth-required') throw reauthErr
        // Current password was correct; the account has MFA, so
        // Firebase needs the authenticator code to finish re-auth.
        const resolver = getMultiFactorResolver(auth, reauthErr as MultiFactorError)
        const totpHint = resolver.hints.find((h) => h.factorId === TotpMultiFactorGenerator.FACTOR_ID)
        if (!totpHint) throw Object.assign(new Error('No authenticator app factor'), { code: 'no-totp-factor' })
        if (pwMfaCode.length !== 6) {
          setPwCodeRequested(true)
          throw Object.assign(new Error('Code needed'), { code: 'mfa-code-needed' })
        }
        await resolver.resolveSignIn(TotpMultiFactorGenerator.assertionForSignIn(totpHint.uid, pwMfaCode.trim()))
      }

      // Update password
      await updatePassword(firebaseUser, newPassword)

      setSuccessMsg('Password changed successfully')
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setPwMfaCode('')
    } catch (err: any) {
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setError('Current password is incorrect')
      } else if (err.code === 'mfa-code-needed') {
        setError('Enter the 6-digit code from your authenticator app')
      } else if (err.code === 'auth/invalid-verification-code') {
        setError('That code is incorrect or has expired — check the time on your device and try the next code.')
        setPwMfaCode('')
      } else if (err.code === 'no-totp-factor') {
        setError("This account's second factor isn't an authenticator app — sign out and sign in again, then retry.")
      } else if (err.code === 'auth/too-many-requests') {
        setError('Too many attempts — please wait a few minutes and try again.')
      } else if (err.code === 'auth/weak-password') {
        setError('Password is too weak — use at least 8 characters')
      } else if (err.code === 'auth/requires-recent-login') {
        setError('Session expired — please log out and log in again')
      } else {
        setError('Failed to change password — try again')
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="max-w-2xl">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white">Settings</h1>
        <p className="text-gray-400 text-sm mt-1">Manage your account</p>
      </div>

      {/* Profile Info */}
      <div className="bg-surface-card border border-border-light rounded-2xl p-6 mb-6">
        <h2 className="text-lg font-semibold text-white mb-4">Profile</h2>
        <div className="flex items-center gap-4 mb-4">
          <div className="w-16 h-16 rounded-full bg-brand-700 flex items-center justify-center text-white text-2xl font-bold">
            {user?.firstName?.[0]?.toUpperCase() || '?'}
          </div>
          <div>
            <p className="text-lg font-semibold text-white">{user?.firstName} {user?.lastName}</p>
            <p className="text-sm text-gray-400">{user?.email}</p>
            <span className="inline-block mt-1 px-2 py-0.5 text-xs rounded-full bg-brand-600/20 text-brand-400">
              {getAdminRole()}
            </span>
          </div>
        </div>
      </div>

      {/* Two-Factor Authentication — available to every admin role,
          including super_admin (nothing here touches the password). */}
      <div className="bg-surface-card border border-border-light rounded-2xl p-6 mb-6">
        <h2 className="text-lg font-semibold text-white mb-2">Two-Factor Authentication</h2>
        <p className="text-sm text-gray-400 mb-4">
          Add an authenticator app (Google Authenticator, Microsoft Authenticator, Authy, or any similar app) as a second sign-in step.
        </p>

        {mfaError && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4">
            <p className="text-red-400 text-sm">{mfaError}</p>
          </div>
        )}

        {mfaStep === 'done' ? (
          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
            <p className="text-green-400 text-sm">✅ Authenticator app enabled — you'll be asked for a code the next time you sign in.</p>
          </div>
        ) : mfaEnrolled ? (
          <div className="flex items-center gap-2 px-3 py-2 bg-green-500/10 border border-green-500/30 rounded-lg w-fit">
            <span className="text-green-400 text-sm">✅ Authenticator app enabled</span>
          </div>
        ) : mfaStep === 'idle' ? (
          <button onClick={startMfaSetup} disabled={mfaBusy}
            className="px-4 py-2.5 text-sm bg-brand-600 hover:bg-brand-500 disabled:opacity-50 text-white rounded-xl transition-colors">
            {mfaBusy ? 'Starting...' : 'Set up authenticator app'}
          </button>
        ) : mfaStep === 'need-reauth' ? (
          <form onSubmit={submitMfaReauth} className="max-w-sm">
            <p className="text-sm text-gray-400 mb-3">For your security, confirm your password before setting up two-factor authentication.</p>
            <input type="password" value={mfaReauthPassword} onChange={(e) => setMfaReauthPassword(e.target.value)}
              placeholder="Current password" required autoFocus
              className="w-full bg-surface-tertiary border border-border-light rounded-xl px-4 py-2.5 text-white placeholder-gray-500 focus:border-brand-500 focus:outline-none mb-3" />
            <div className="flex gap-2">
              <button type="button" onClick={cancelMfaSetup}
                className="px-4 py-2 text-sm bg-surface-tertiary text-gray-300 rounded-xl hover:bg-border-light transition-colors">Cancel</button>
              <button type="submit" disabled={mfaBusy}
                className="px-4 py-2 text-sm bg-brand-600 text-white rounded-xl hover:bg-brand-500 disabled:opacity-50 transition-colors">
                {mfaBusy ? 'Verifying...' : 'Continue'}
              </button>
            </div>
          </form>
        ) : (
          // mfaStep === 'scan'
          <div className="max-w-sm">
            <p className="text-sm text-gray-300 mb-3">1. Scan this QR code with your authenticator app:</p>
            {mfaQrDataUrl && (
              <img src={mfaQrDataUrl} alt="Scan with your authenticator app" className="w-48 h-48 rounded-lg border border-border-light mb-3" />
            )}
            <p className="text-xs text-gray-500 mb-1">Can't scan it? Enter this code manually instead:</p>
            <p className="text-xs font-mono text-gray-300 bg-surface-tertiary rounded-lg px-3 py-2 mb-4 break-all">{mfaSecretKey}</p>

            <form onSubmit={submitMfaCode}>
              <p className="text-sm text-gray-300 mb-2">2. Enter the 6-digit code it shows:</p>
              <input type="text" inputMode="numeric" pattern="[0-9]*" value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="123456" required maxLength={6}
                className="w-full bg-surface-tertiary border border-border-light rounded-xl px-4 py-3 text-white text-center text-xl tracking-[0.4em] placeholder-gray-600 focus:border-brand-500 focus:outline-none mb-3" />
              <div className="flex gap-2">
                <button type="button" onClick={cancelMfaSetup}
                  className="px-4 py-2 text-sm bg-surface-tertiary text-gray-300 rounded-xl hover:bg-border-light transition-colors">Cancel</button>
                <button type="submit" disabled={mfaBusy || mfaCode.length !== 6}
                  className="px-4 py-2 text-sm bg-brand-600 text-white rounded-xl hover:bg-brand-500 disabled:opacity-50 transition-colors">
                  {mfaBusy ? 'Confirming...' : 'Confirm & Enable'}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>

      {/* Change Password — not available for super_admin (use Firebase Console) */}
      {!user?.roles.includes('super_admin') && (
      <div className="bg-surface-card border border-border-light rounded-2xl p-6">
        <h2 className="text-lg font-semibold text-white mb-4">Change Password</h2>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}
        {successMsg && (
          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 mb-4">
            <p className="text-green-400 text-sm">{successMsg}</p>
          </div>
        )}

        <form onSubmit={handleChangePassword}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-300 mb-2">Current Password</label>
            <input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Enter current password" required
              className="w-full bg-surface-tertiary border border-border-light rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:border-brand-500 focus:outline-none" />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-300 mb-2">New Password</label>
            <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password (min 8 characters)" required minLength={8}
              className="w-full bg-surface-tertiary border border-border-light rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:border-brand-500 focus:outline-none" />
          </div>
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-300 mb-2">Confirm New Password</label>
            <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password" required
              className="w-full bg-surface-tertiary border border-border-light rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:border-brand-500 focus:outline-none" />
          </div>
          {showPwCode && (
            <div className="mb-6 -mt-2">
              <label className="block text-sm font-medium text-gray-300 mb-2">Authenticator Code</label>
              <input type="text" inputMode="numeric" pattern="[0-9]*" autoComplete="one-time-code"
                value={pwMfaCode} onChange={(e) => setPwMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="6-digit code from your authenticator app" maxLength={6}
                className="w-full bg-surface-tertiary border border-border-light rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:border-brand-500 focus:outline-none" />
            </div>
          )}
          <button type="submit" disabled={isLoading}
            className="bg-brand-600 hover:bg-brand-500 disabled:opacity-50 text-white font-semibold rounded-xl px-6 py-3 transition-colors">
            {isLoading ? 'Changing...' : 'Change Password'}
          </button>
        </form>
      </div>
      )}

      {user?.roles.includes('super_admin') && (
        <div className="bg-surface-card border border-border-light rounded-2xl p-6">
          <h2 className="text-lg font-semibold text-white mb-2">Change Password</h2>
          <p className="text-sm text-gray-400">Super admin passwords are managed via the Firebase Console for security reasons.</p>
        </div>
      )}
    </div>
  )
}

export default SettingsPage